'use client';

import { useCallback } from 'react';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { Card, CardActionArea, Stack, Typography, Chip } from '@mui/material';
import { ImageWithFallback } from '@qb/frontend/components/ImageWithFallback';
import { clampLinesSx } from '@qb/frontend/utils/clampLinesSx';
import { TenantConfig } from '@/shared/types/controllers/sps/SPSAppControllerTypes';
import { PartPopularPartsDataResponse } from '@/shared/types/controllers/sps/SPSPartControllerTypes';
import { generatePartURL } from '@/shared/utils/URLs';
type PartCardSmallProps = {
  tenantConfig: TenantConfig;
  part: PartPopularPartsDataResponse['parts'][0];
  onClick?: () => void;
};
export const PartCardSmall = ({
  tenantConfig,
  part,
  onClick
}: PartCardSmallProps) => {
  const combinedStock = part.onHandQty + part.manufacturerStock;
  const href = generatePartURL(part.manufacturer.name, part.partNumber, part.id);
  const onPartView = useCallback(() => {
    if (onClick) {
      onClick();
    }
  }, [onClick]);
  return <Card sx={{
    borderWidth: '2px',
    borderColor: 'primary.50p',
    mb: {
      xs: 1,
      borderRadius: 0
    }
  }} data-sentry-element="Card" data-sentry-component="PartCardSmall" data-sentry-source-file="PartCardSmall.tsx">
      <CardActionArea onClick={onPartView} href={href} sx={{
      pl: 1.5,
      pr: 2,
      py: 1
    }} data-sentry-element="CardActionArea" data-sentry-source-file="PartCardSmall.tsx">
        <Stack direction="row" spacing={2} sx={{
        minWidth: 200
      }} data-sentry-element="Stack" data-sentry-source-file="PartCardSmall.tsx">
          <ImageWithFallback tenantAlias={tenantConfig.tenantAlias} src={part.images[0]?.target} width={50} height={50} alt={`${part.partNumber} on ${tenantConfig.companyName}`} data-sentry-element="ImageWithFallback" data-sentry-source-file="PartCardSmall.tsx" />
          <Stack direction="column" sx={{
          width: '100%',
          overflow: 'hidden'
        }} data-sentry-element="Stack" data-sentry-source-file="PartCardSmall.tsx">
            <Typography variant="bodySmallPrimary" component="h3" sx={{
            color: 'primary.main'
          }} data-sentry-element="Typography" data-sentry-source-file="PartCardSmall.tsx">
              {part.partNumber}
            </Typography>
            <Typography variant="bodySmallPrimary" component="h3" sx={{
            color: 'text.primary',
            mb: 0.5
          }} data-sentry-element="Typography" data-sentry-source-file="PartCardSmall.tsx">
              {part.manufacturer.name}
            </Typography>
            <Typography title={part.description} variant="bodySmall" sx={{
            mb: 1,
            height: 42,
            ...clampLinesSx(3)
          }} data-sentry-element="Typography" data-sentry-source-file="PartCardSmall.tsx">
              {part.description}
            </Typography>
            <Chip size="small" icon={<CheckCircleIcon />} label={`${combinedStock} in stock`} sx={{
            visibility: combinedStock > 0 ? 'visible' : 'hidden',
            height: 21,
            mr: 'auto',
            bgcolor: '#00ED8E',
            color: 'text.primary',
            '.MuiChip-icon': {
              color: 'text.primary'
            },
            '.MuiChip-label': {}
          }} data-sentry-element="Chip" data-sentry-source-file="PartCardSmall.tsx" />
          </Stack>
        </Stack>
      </CardActionArea>
    </Card>;
};